// src/services/error-reporter.ts
//
// Turns an internal failure into something a user can actually report.
//
// The production console is silenced (see the guard in public/index.html) and
// everything console.* emits is shipped to CloudWatch by services/remote-logger.
// So the detail is never lost — it just should not be on screen. Showing a user
// `Cannot query field "sendOrgUserPasswordReset" on type "Mutation"` tells them
// nothing they can act on, and tells anyone looking over their shoulder about
// our schema.
//
// Instead: log the whole thing against a short reference, show them the
// reference, and let support search CloudWatch for it.

/**
 * Crockford-style alphabet: no I, L, O, U, 0 or 1, so a reference read aloud
 * down a phone line or typed from a screenshot cannot be ambiguous.
 */
const ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";

function randomValues(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  try {
    // Not for secrecy — just so two users hitting the same bug in the same
    // millisecond do not report the same reference.
    (window.crypto || (window as unknown as { msCrypto: Crypto }).msCrypto).getRandomValues(bytes);
  } catch (e) {
    for (let i = 0; i < length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return bytes;
}

/** e.g. "LX-4F2A-9C7B" — short enough to read out, long enough not to collide. */
export function newErrorReference(): string {
  const chars = Array.from(randomValues(8), (b) => ALPHABET[b % ALPHABET.length]);
  return `LX-${chars.slice(0, 4).join("")}-${chars.slice(4).join("")}`;
}

/**
 * One failure must produce ONE reference.
 *
 * The Apollo error link sees a failure first and the component's catch sees it
 * second, so both used to mint their own — the dialog quoted one id and the
 * toast another, for a single event, and neither found the whole story in
 * CloudWatch. The link now stamps the reference onto the very error objects
 * Apollo hands onward, and the catch reuses it.
 */
const REFERENCE_KEY = "__luxReference";

export function attachReference(target: unknown, reference: string): void {
  if (!target || typeof target !== "object") return;
  try {
    Object.defineProperty(target, REFERENCE_KEY, {
      value: reference,
      enumerable: false,
      configurable: true,
    });
  } catch (e) {
    /* frozen object — the caller falls back to a fresh reference */
  }
}

/** First reference already stamped on any of these, if there is one. */
export function existingReference(...candidates: unknown[]): string | undefined {
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object") {
      const found = (candidate as Record<string, unknown>)[REFERENCE_KEY];
      if (typeof found === "string") return found;
    }
  }
  return undefined;
}

/** Everything we know about the failure, flattened for a single log line. */
function detail(error: unknown): string {
  if (!error) return "(no error object)";
  if (typeof error === "string") return error;
  const err = error as {
    name?: string;
    message?: string;
    stack?: string;
    graphQLErrors?: { message?: string; extensions?: { code?: string } }[];
    networkError?: { statusCode?: number; message?: string };
  };
  const parts: string[] = [];
  if (err.name) parts.push(`name=${err.name}`);
  if (err.message) parts.push(`message=${err.message}`);
  if (err.networkError) {
    parts.push(`network=${err.networkError.statusCode ?? "?"} ${err.networkError.message ?? ""}`);
  }
  err.graphQLErrors?.forEach((g) => {
    parts.push(`graphql[${g.extensions?.code ?? "?"}]=${g.message ?? ""}`);
  });
  if (err.stack) parts.push(`stack=${err.stack}`);
  return parts.join(" | ") || String(error);
}

/**
 * Log a failure against a reference and return it.
 *
 * `context` should say what the user was trying to do ("send password reset"),
 * not what broke — that is what the error itself carries.
 *
 * `actor` is the signed-in user's Cognito `sub`. Support's first question is
 * always "which user", and the log line otherwise carries only a page URL and
 * an anonymous session id. The caller supplies it rather than this module
 * reading the store: the store imports vue-apollo, which imports this file, so
 * reaching back for it here would close an import cycle. The `sub` rather than
 * the email address — it identifies the account exactly and is resolvable from
 * the admin screens, without writing personal data into a broadly-readable log
 * group.
 *
 * Reuses a reference already stamped on the error, so one failure never gets
 * two ids and is never logged twice.
 */
export function reportError(error: unknown, context: string, actor?: string): string {
  const already = existingReference(error);
  if (already) return already;

  const reference = newErrorReference();
  attachReference(error, reference);
  // console.error, not a bespoke transport: index.html replaces console.* with
  // a capturing buffer and remote-logger ships it to CloudWatch with the page
  // URL and session id attached.
  console.error(
    `[${reference}] ${context}${actor ? ` user=${actor}` : ""} — ${detail(error)}`
  );
  return reference;
}

/**
 * The sentence to put on screen. Deliberately says nothing about the cause.
 */
export function referenceMessage(reference: string, supportEmail?: string): string {
  const where = supportEmail ? ` Please send this to ${supportEmail}.` : "";
  return `Something went wrong on our side. Reference ${reference}.${where}`;
}

/** Log the failure and return the sentence to display, in one call. */
export function reportAndDescribe(
  error: unknown,
  context: string,
  supportEmail?: string,
  actor?: string
): string {
  return referenceMessage(reportError(error, context, actor), supportEmail);
}

/**
 * GraphQL error codes that mean OUR machinery is wrong, not the user.
 *
 * A validation failure like `Cannot query field "x" on type "Mutation"` means
 * the deployed client is asking for something the deployed API does not have —
 * a split deploy, invariably. There is nothing a user can do with that text.
 *
 * Everything NOT in this set is treated as intentionally user-facing, because
 * resolvers do raise messages meant to be read ("Forbidden: cross-organization
 * access is not allowed", "This user has no email address to send a reset to").
 * Hiding those behind a reference would be a regression.
 */
const INTERNAL_CODES = new Set([
  "GRAPHQL_VALIDATION_FAILED",
  "GRAPHQL_PARSE_FAILED",
  "PERSISTED_QUERY_NOT_FOUND",
  "PERSISTED_QUERY_NOT_SUPPORTED",
  // Raised by the backend's ServerMisconfiguredError. The message names an
  // environment variable, which nobody using the app can act on — "Cannot send
  // a reset link: APP_BASE_URL is not configured" is our problem, not theirs.
  "SERVER_MISCONFIGURED",
]);

export function isInternalGraphQLError(code?: string): boolean {
  return Boolean(code && INTERNAL_CODES.has(code));
}

/**
 * What to show for a failed Apollo call: the resolver's own words when it wrote
 * some, a quotable reference when it did not.
 *
 * Use this instead of `error.message` in a catch. On an ApolloError that field
 * is the TRANSPORT summary — "Response not successful: Received status code
 * 400" — which is arguably worse than the schema error it is hiding, because it
 * looks like a server outage when the real cause is a client/API version skew.
 */
export function describeApolloError(
  error: unknown,
  context: string,
  supportEmail?: string,
  actor?: string
): string {
  const apollo = error as {
    graphQLErrors?: { message?: string; extensions?: { code?: string } }[];
    networkError?: { result?: { errors?: { message?: string; extensions?: { code?: string } }[] } };
  };

  // A GraphQL validation failure comes back as HTTP 400, which Apollo surfaces
  // as a networkError whose `message` is only "Response not successful:
  // Received status code 400". The real complaint is in networkError.result.
  const all = [...(apollo?.graphQLErrors ?? []), ...(apollo?.networkError?.result?.errors ?? [])];

  const meaningful = all.find(
    (g) => g.message && !isInternalGraphQLError(g.extensions?.code as string | undefined)
  );
  if (meaningful?.message) return meaningful.message;

  // Reuse whatever the Apollo error link already stamped and logged, so the
  // dialog and the toast quote the SAME id.
  const stamped = existingReference(error, apollo?.networkError, ...all);
  if (stamped) return referenceMessage(stamped, supportEmail);

  return reportAndDescribe(error, context, supportEmail, actor);
}
